import React, { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RT,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { convertirMonto, formatMonedaSimbolo } from '../../libs/moneda';
import type { HistoryPoint } from '../../libs/publicPricesSnapshot';
import CurrencyToggle, { type Currency } from './CurrencyToggle';
import '../landing/brand.css';

// Colores del manual de marca v1.0: Verde Campo, Brote, Trigo y Piedra se
// distinguen entre sí (Pastura queda afuera: con Verde Campo se confunden).
const SERIES = [
  { key: 'novillos', name: 'Novillos', color: '#193D2A' },
  { key: 'terneros', name: 'Terneros', color: '#7FB069' },
  { key: 'vaquillonas', name: 'Vaquillonas', color: '#D9A441' },
  { key: 'terneras', name: 'Terneras', color: '#6E756B' },
] as const;

const NIEBLA = '#DDE1D8';

/**
 * Tendencia de precio ($/kg promedio mensual) desde el `history[]` del snapshot.
 * Toggle ARS/USD: al pasar a USD divide cada mes por su `usd` (cotización de ese mes).
 */
const PrecioTrendChart: React.FC<{
  history: HistoryPoint[];
  usdRate: number | null; // cotización más reciente (solo para mostrar en el toggle)
  uvaToday?: number | null; // CER más reciente (para modo constante)
  height?: number;
  /** Mostrar el toggle de moneda (default true). */
  showCurrencyToggle?: boolean;
  /** Subtítulo del chart (default "$/kg promedio mensual"). */
  subtitle?: string;
}> = ({ history, usdRate, uvaToday = null, height = 320, showCurrencyToggle = true, subtitle = '$/kg promedio mensual' }) => {
  const [currency, setCurrency] = useState<Currency>('ARS');
  const hasUsd = history.some((h) => h.usd);
  const hasUva = !!uvaToday && history.some((h) => h.uva);

  const fmt = (v: number) => formatMonedaSimbolo(v, currency);

  const data = history.map((h) => {
    const conv = (ars: number | null) =>
      ars == null ? null : convertirMonto(ars, currency, h.usd, uvaToday, h.uva);
    return {
      label: h.label,
      terneros: conv(h.terneros),
      terneras: conv(h.terneras),
      novillos: conv(h.novillos),
      vaquillonas: conv(h.vaquillonas),
    };
  });

  return (
    <div className="lp-scope lp-card lp-chart">
      <div className="lp-card__h">
        <p className="lp-note">{subtitle}</p>
        {showCurrencyToggle && (
          <CurrencyToggle value={currency} onChange={setCurrency} disabled={!hasUsd} usdRate={usdRate} uvaAvailable={hasUva} />
        )}
      </div>
      <div style={{ height }}>
        {data.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke={NIEBLA} />
              <XAxis dataKey="label" tickLine={false} axisLine={{ stroke: NIEBLA }} tickMargin={8} />
              <YAxis width={64} tickFormatter={(v) => fmt(Number(v))} tickLine={false} axisLine={false} />
              <RT
                cursor={{ stroke: NIEBLA, strokeWidth: 1 }}
                formatter={(v: number | string) => fmt(Number(v))}
                contentStyle={{ background: '#FFFFFF', border: `1px solid ${NIEBLA}`, borderRadius: 8, fontSize: 13, color: '#1C1F1A' }}
              />
              <Legend iconType="plainline" wrapperStyle={{ paddingTop: 12 }} />
              {SERIES.map((s) => (
                <Line
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.name}
                  stroke={s.color}
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ r: 4 }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="lp-note" style={{ display: 'grid', placeItems: 'center', height: '100%' }}>
            Sin datos históricos.
          </div>
        )}
      </div>
    </div>
  );
};

export default PrecioTrendChart;
